import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, Container, Grid,
  Card, CardContent, CardActions, Box, IconButton, Avatar,
  Menu, MenuItem, Divider,
} from '@mui/material';
import {
  PersonAdd, EventAvailable, LocalHospital, Hotel, People,
  Medication, Image, ExitToApp, LocalPrintshop, CalendarMonth,
  Science, Receipt, MedicalServices, Analytics, Assignment,
  EventNote, BeachAccess, AccountBalance, Payments,
  MedicalInformation, WorkspacePremium,
} from '@mui/icons-material';
import { authService } from '../services/api';

const DashboardPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const getGroups = () => {
    const role = user?.role;
    const groups = [];

    // ── Clinical ────────────────────────────────────────────────────────
    const clinical = [];

    if (role === 'Receptionist' || role === 'Doctor' || role === 'Admin') {
      clinical.push(
        { title: 'Billing',            description: 'Generate and print patient bills',    icon: <Receipt sx={{ fontSize: 46 }} />,       path: '/billing',            color: '#f57c00' },
        { title: 'Patient Management', description: 'Register and manage patients',         icon: <PersonAdd sx={{ fontSize: 46 }} />,     path: '/patient-management', color: '#2196f3' },
        { title: 'Appointments',       description: 'Schedule and manage appointments',     icon: <EventAvailable sx={{ fontSize: 46 }} />, path: '/appointments',       color: '#4caf50' },
        { title: 'OBG – EDD Calendar', description: 'View Expected Delivery Dates',         icon: <CalendarMonth sx={{ fontSize: 46 }} />,  path: '/obg-edd-calendar',   color: '#e91e63' },
      );
    }

    if (role === 'Receptionist' || role === 'Doctor' || role === 'LabAttendant' || role === 'Manager') {
      clinical.push(
        { title: 'My Leaves', description: 'Request and track your leave applications', icon: <BeachAccess sx={{ fontSize: 46 }} />, path: '/my-leaves', color: '#e65100' },
      );
    }

    if (role === 'Doctor' || role === 'Admin') {
      clinical.push(
        { title: 'OPD',     description: 'Outpatient Department queue',  icon: <LocalHospital sx={{ fontSize: 46 }} />, path: '/opd',     color: '#ff9800' },
        { title: 'IPD',     description: 'Inpatient Department queue',   icon: <Hotel sx={{ fontSize: 46 }} />,        path: '/ipd',     color: '#9c27b0' },
        { title: 'Reports', description: 'Generate medical certificates', icon: <Assignment sx={{ fontSize: 46 }} />,   path: '/reports', color: '#5c6bc0' },
      );
    }

    if (clinical.length) groups.push({ label: 'Clinical', color: '#1565c0', items: clinical });

    // ── Manager ─────────────────────────────────────────────────────────
    if (role === 'Manager') {
      groups.push({
        label: 'Management',
        color: '#4527a0',
        items: [
          { title: 'Expenses',  description: 'Record and track hospital expenses', icon: <Receipt sx={{ fontSize: 46 }} />,     path: '/admin/expenses', color: '#c62828' },
        ],
      });
    }

    // ── People Management (Admin only) ──────────────────────────────────
    if (role === 'Admin') {
      groups.push({
        label: 'People Management',
        color: '#00695c',
        items: [
          { title: 'Doctor Management',  description: 'Add and manage doctors',          icon: <MedicalInformation sx={{ fontSize: 46 }} />, path: '/admin/doctors',     color: '#00bcd4' },
          { title: 'Staff Management',   description: 'Add and manage staff members',    icon: <People sx={{ fontSize: 46 }} />,             path: '/admin/staff',       color: '#795548' },
          { title: 'Refer Doctors',      description: 'Manage refer doctor directory',   icon: <WorkspacePremium sx={{ fontSize: 46 }} />,   path: '/admin/refer-doctors', color: '#00838f' },
          { title: 'Attendance',         description: 'Mark and view daily attendance',  icon: <EventNote sx={{ fontSize: 46 }} />,          path: '/admin/attendance',  color: '#0277bd' },
          { title: 'Leave Management',   description: 'Record and manage staff leaves',  icon: <BeachAccess sx={{ fontSize: 46 }} />,        path: '/admin/leaves',      color: '#e65100' },
        ],
      });

      // ── Finance ──────────────────────────────────────────────────────
      groups.push({
        label: 'Finance',
        color: '#2e7d32',
        items: [
          { title: 'Salary Structure',   description: 'Set monthly salary components per staff', icon: <AccountBalance sx={{ fontSize: 46 }} />, path: '/admin/salary-structure',  color: '#4527a0' },
          { title: 'Salary Processing',  description: 'Generate and print monthly salary slips',  icon: <Payments sx={{ fontSize: 46 }} />,      path: '/admin/salary-processing', color: '#2e7d32' },
          { title: 'Expenses',           description: 'Record and track hospital expenses',       icon: <Receipt sx={{ fontSize: 46 }} />,       path: '/admin/expenses',          color: '#c62828' },
        ],
      });

      // ── Settings & Configuration ─────────────────────────────────────
      groups.push({
        label: 'Settings & Configuration',
        color: '#546e7a',
        items: [
          { title: 'Print Settings',     description: 'Configure letterhead and margins',     icon: <LocalPrintshop sx={{ fontSize: 46 }} />, path: '/admin/print-settings',   color: '#607d8b' },
          { title: 'Medicines & Advice', description: 'Manage medicines and advice master',   icon: <Medication sx={{ fontSize: 46 }} />,     path: '/admin/medicines',        color: '#e91e63' },
          { title: 'Investigations',     description: 'Manage investigation catalogue',       icon: <Science sx={{ fontSize: 46 }} />,        path: '/admin/investigations',   color: '#009688' },
          { title: 'Services & Rates',   description: 'Manage service rates for billing',    icon: <MedicalServices sx={{ fontSize: 46 }} />, path: '/admin/services',         color: '#ff5722' },
          { title: 'Media Management',   description: 'Upload and manage media files',        icon: <Image sx={{ fontSize: 46 }} />,          path: '/admin/media',            color: '#3f51b5' },
        ],
      });

      // ── Analytics ─────────────────────────────────────────────────────
      groups.push({
        label: 'Analytics',
        color: '#6a1b9a',
        items: [
          { title: 'Hospital Analytics', description: 'Revenue, payroll and doctor performance dashboards', icon: <Analytics sx={{ fontSize: 46 }} />, path: '/admin/analytics', color: '#6a1b9a' },
        ],
      });
    }

    return groups;
  };

  const groups = getGroups();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Dr. Dhawal Sawant - Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName} ({user?.role})
            </Typography>
            <IconButton size="large" onClick={e => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 5, textAlign: 'center' }}>
          Welcome, {user?.firstName}!
        </Typography>

        {groups.map((group, gi) => (
          <Box key={group.label} sx={{ mb: gi < groups.length - 1 ? 5 : 2 }}>
            {/* Section header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
              <Box sx={{ width: 5, height: 28, borderRadius: 1, bgcolor: group.color, flexShrink: 0 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: group.color, letterSpacing: 0.5 }}>
                {group.label}
              </Typography>
              <Divider sx={{ flex: 1, borderColor: group.color, opacity: 0.25 }} />
            </Box>

            <Grid container spacing={3}>
              {group.items.map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.title + item.path}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 10px 28px rgba(0,0,0,0.18)`,
                      },
                    }}
                    onClick={() => navigate(item.path)}
                  >
                    <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 3.5, pb: 1 }}>
                      <Box sx={{ color: item.color, mb: 1.5 }}>{item.icon}</Box>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {item.description}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'center', pb: 2.5 }}>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          backgroundColor: item.color,
                          '&:hover': { backgroundColor: item.color, filter: 'brightness(0.9)' },
                          px: 2.5,
                        }}
                      >
                        Open
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Container>
    </div>
  );
};

export default DashboardPage;
